import { useEffect } from "react";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { redeemOfferAction } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import { setLoyaltyTermsAndConditionsDrawer } from "../../../services/redux/slices/globalComponentsToggleSlice";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import {
  getPointingSystemName,
  localizedNumber,
} from "../../../utils/help/localization/localization";
import { PercentageAmountOfferType } from "../../../utils/types";
import { CloseIcon } from "../Icons/Icons";
import { MileStone } from "../Icons/loyalty.icons";
import styles from "./TermsAndConditionsDrawerComponent.module.scss";
import { NavigationBar } from "./navigationBar/NavigationBar";

export const TermsAndConditionsDrawerComponent = () => {
  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const redeemText = t("pointing-system:redeem");
  const drawerState = useAppSelector(
    (state) =>
      state.globalComponentsToggleSlice.loyaltyTermsAndConditionsDrawer
        .isOpen || false
  );
  const selectedMilestoneIndex = useAppSelector(
    (state) =>
      state.globalComponentsToggleSlice.loyaltyTermsAndConditionsDrawer
        .selectedMilestone
  );

  const currency = useAppSelector((state) => state.config.currency);

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);

  const redeemedOffer = cartAndLoyaltyReducer.offersArray.find((o) =>
    o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
  );

  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const pointingSystem = cartAndLoyaltyReducer.pointingSystem;
  const currentMileStones = pointingSystem?.restaurantPackageOffers;

  const milestonePoints =
    currentMileStones &&
    currentMileStones[selectedMilestoneIndex].pointingMilestone?.points;

  const currentOffer =
    currentMileStones && currentMileStones[selectedMilestoneIndex].packageOffer;

  const offerNotApplicable = !currentOffer!.isOfferApplicable({
    cart: cartLoyaltyV2.cart,
    itemStatusRedux,
    orderType: orderTypeParam,
    timeZone: restaurantInfo?.timeZone!,
  });

  const eligibleMilestone =
    pointingSystem &&
    milestonePoints &&
    pointingSystem?.customerCurrentPoints >= milestonePoints;

  const activeOffer = redeemedOffer?.id === currentOffer?.id;

  const someAddonsI18 = t("pointing-system:some-add-ons");
  const offerDetailsI18 = t("pointing-system:offer-details");
  const cappedAtI18 = t("pointing-system:capped-at");
  const canOnlyI18 = t("pointing-system:can-only-be-redeemed");
  const canBeRedeemedI18 = t("pointing-system:can-be-redeemed-worth");

  const pointName = getPointingSystemName(
    cartAndLoyaltyReducer.pointingSystem!
  );

  const dispatch = useAppDispatch();

  const redeemOffer = () => {
    if (currentOffer?.isItemFromListOffer()) {
      handleModal(true, "ItemFromList/ItemFromList", {
        componentProps: { offer: currentOffer },
      });
    } else if (currentOffer) {
      dispatch(redeemOfferAction({ offer: currentOffer }));
    }
    dispatch(
      setLoyaltyTermsAndConditionsDrawer({
        isOpen: false,
        selectedMilestone: 0,
      })
    );
  };

  useEffect(() => {
    // Add or remove the "overflow-hidden"
    // class based on the drawer state tp prevent background scrolling
    const body = document.body;
    if (drawerState) {
      body.classList.add("overflow-hidden");
    } else {
      body.classList.remove("overflow-hidden");
    }

    return () => {
      body.classList.remove("overflow-hidden");
    };
  }, [drawerState]);

  return (
    <div>
      {drawerState && (
        <div
          className={styles.backdrop}
          onClick={() =>
            dispatch(
              setLoyaltyTermsAndConditionsDrawer({
                isOpen: false,
                selectedMilestone: 0,
              })
            )
          }
        ></div>
      )}

      <div
        data-testid="terms-conditions-drawer"
        className={`${styles.drawer_wrapper}`}
        style={{
          animationName: drawerState ? styles.open_drawer : styles.close_drawer,
        }}
      >
        <div
          className={styles.close}
          data-testid="terms-conditions-drawer-close"
          onClick={() =>
            dispatch(
              setLoyaltyTermsAndConditionsDrawer({
                isOpen: false,
                selectedMilestone: 0,
              })
            )
          }
        >
          <CloseIcon />
        </div>
        <div className={styles.slider_data_title}>{offerDetailsI18}</div>

        <NavigationBar />
        <span className={styles.offer_title}>
          {currentOffer &&
            (Offer.getLocalizedValue(currentOffer.uiDetails?.description) ??
              Offer.getLocalizedValue(currentOffer.uiDetails?.titleText))}
        </span>
        <div className={styles.slider_data}>
          {currentOffer?.isItemFromListOffer() && (
            <div className={styles.term}>
              <MileStone />
              <span>
                {Offer.getLocalizedValue(currentOffer.uiDetails?.titleText)}
              </span>
            </div>
          )}
          <div className={styles.term}>
            <MileStone />
            <span>
              {canBeRedeemedI18}{" "}
              <span className={styles.terms_points}>
                {localizedNumber(milestonePoints)}
              </span>{" "}
              {pointName}
            </span>
          </div>
          <div className={styles.term}>
            <MileStone />
            {canOnlyI18}
          </div>
          {currentOffer?.isPercentageOffer() && (
            <div className={styles.term}>
              <MileStone />
              {cappedAtI18} {currency}
              {(currentOffer as PercentageAmountOfferType)?.details?.cap}
            </div>
          )}
          {currentOffer?.isFreeItemOffer() && (
            <div className={styles.term}>
              <MileStone />
              {someAddonsI18}
            </div>
          )}
          {offerNotApplicable && (
            <div className={`${styles.term} ${styles.warn}`}>
              {t(
                currentOffer!.isItemOffer()
                  ? "common:items-unavailable"
                  : "offers:offer-unavailable"
              )}
            </div>
          )}
        </div>
        <button
          disabled={!eligibleMilestone || activeOffer || offerNotApplicable}
          className={styles.redeem_btn}
          onClick={redeemOffer}
          data-testid="drawer-redeem-button"
        >
          {redeemText}
        </button>
      </div>
    </div>
  );
};
